import { useEffect } from 'react'
import { ModalForm } from '@ant-design/pro-components'
import { useMutation } from '@tanstack/react-query'
import { App, Form, Input, Tag } from 'antd'
import { CUSTOMER_DEFAULT_TAG_COLOR } from '@super-mega/server/contracts/customer'
import { useTRPC } from '@/trpc/react.ts'
import { getErrorMessage } from '@/utils/error.ts'
import { CustomerTagColorPicker } from './customer-tag-color-picker.tsx'

export type CustomerTagEditorRecord = {
  id: number
  name: string
  color: string
}

export type CustomerTagFormValues = {
  name: string
  color: string
}

function getInitialValues(editing?: CustomerTagEditorRecord | 'new'): CustomerTagFormValues {
  return editing && editing !== 'new'
    ? { color: editing.color, name: editing.name }
    : { color: CUSTOMER_DEFAULT_TAG_COLOR, name: '' }
}

export function CustomerTagEditorModal({
  editing,
  onClose,
  onSaved
}: {
  editing?: CustomerTagEditorRecord | 'new'
  onClose: () => void
  onSaved: (tag: CustomerTagEditorRecord) => Promise<void>
}) {
  const trpc = useTRPC()
  const { message } = App.useApp()
  const [form] = Form.useForm<CustomerTagFormValues>()
  const upsert = useMutation(trpc.customer.upsertTag.mutationOptions())
  const tagName = Form.useWatch('name', form) as string | undefined
  const tagColor = (Form.useWatch('color', form) as string | undefined) ?? CUSTOMER_DEFAULT_TAG_COLOR
  const initialValues = getInitialValues(editing)

  useEffect(() => {
    if (!editing) {
      return
    }
    form.resetFields()
    form.setFieldsValue(getInitialValues(editing))
  }, [editing, form])

  const save = async (values: CustomerTagFormValues) => {
    if (upsert.isPending) {
      return false
    }
    try {
      const saved = await upsert.mutateAsync({
        id: editing === 'new' ? undefined : editing?.id,
        data: {
          color: values.color,
          name: values.name.trim()
        }
      })
      void message.success(editing === 'new' ? '客户标签已创建' : '客户标签已更新')
      await onSaved(saved)
      return true
    } catch (error) {
      void message.error(getErrorMessage(error, '客户标签保存失败'))
      return false
    }
  }

  return (
    <ModalForm<CustomerTagFormValues>
      form={form}
      initialValues={initialValues}
      layout="vertical"
      modalProps={{ destroyOnHidden: true, mask: { closable: false }, onCancel: onClose }}
      onFinish={save}
      onOpenChange={(open) => {
        if (!open) {
          onClose()
        }
      }}
      open={Boolean(editing)}
      scrollToFirstError
      submitter={{
        resetButtonProps: { onClick: onClose },
        searchConfig: { resetText: '取消', submitText: '保存标签' },
        submitButtonProps: { loading: upsert.isPending }
      }}
      title={editing === 'new' ? '新建客户标签' : `编辑客户标签 · ${editing?.name ?? ''}`}
      width="32rem"
    >
      <Form.Item label="标签名称" name="name" rules={[{ max: 100, required: true, whitespace: true }]}>
        <Input
          maxLength={100}
          placeholder="请输入客户标签名称"
          onPressEnter={(event) => {
            if (event.nativeEvent.isComposing || event.keyCode === 229) {
              return
            }
            event.preventDefault()
            if (!upsert.isPending) {
              form.submit()
            }
          }}
        />
      </Form.Item>
      <Form.Item
        extra={
          <span className="mt-2 flex flex-wrap items-center gap-2 px-1">
            <span>支持自定义颜色，用于客户档案中快速识别。</span>
            <span aria-hidden>预览：</span>
            <Tag color={tagColor}>{tagName?.trim() || '标签名称'}</Tag>
          </span>
        }
        label="标签颜色"
        name="color"
        rules={[{ message: '请选择标签颜色', required: true }]}
      >
        <CustomerTagColorPicker />
      </Form.Item>
    </ModalForm>
  )
}
